import React, { useState } from 'react'
import { Typography, useTheme } from '@mui/material'
import ConfirmationModal from './ConfirmationModal'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import { TextButtonVariant } from '../Buttons/TextButton'
import useEndpoint from '../../hooks/useEndpoint'
import { screening } from '../../api/swagger'
import { RowActionDataUpdate } from '../Legal/UserScreeningTab'
import { useTranslation } from 'react-i18next'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { LoadingContext } from '../Context/LoadingContext'
import UpdateUserScreeningWarningModal from './UpdateUserScreeningWarningModal'

export interface ConfirmUserScreeningStatusUpdateModalProps {
  isOpen: boolean
  onClose: (updatedRecord: boolean) => void
  userScreeningToUpdate: RowActionDataUpdate
}

const ConfirmUserScreeningStatusUpdateModal: React.FC<
  ConfirmUserScreeningStatusUpdateModalProps
> = ({ isOpen, onClose, userScreeningToUpdate }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { loadingIds } = React.useContext(LoadingContext)

  const {
    Legal: { updateUserScreening: updateUserScreeningLoadingId },
  } = useLoadingIds()

  const [warningMessage, setWarningMessage] = useState<string | undefined>()
  const [showHandleSuccessModal, setShowHandleWarningModal] =
    useState<boolean>(false)

  const { triggerFetch: triggerUpdateUserScreening } = useEndpoint({
    loadingId: updateUserScreeningLoadingId,
    swaggerCall: async () => {
      const response = await screening.updateUserScreening({
        body: {
          status: userScreeningToUpdate.status,
          userScreeningKey: userScreeningToUpdate.userScreeningKey,
        },
      })

      const message = response?.warning?.message
      if (message) {
        setWarningMessage(message)
        setShowHandleWarningModal(true)
      }
    },
    successCallback: () => {
      onClose(true)
    },
  })

  return (
    <>
      <ConfirmationModal
        dialogContent={
          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: theme.spacing(2),
            }}
          >
            {t(
              'UserScreeningTab.Modal.ConfirmUserScreeningStatusUpdateModal.Title',
              "Are you sure you want to change the user's {{screeningType}} status to {{statusTo}}?",
              {
                statusTo: userScreeningToUpdate?.status ?? '',
                screeningType: userScreeningToUpdate?.screeningType ?? '',
              }
            )}
          </Typography>
        }
        dialogActions={
          <ActionButtons
            primaryButtonLabel={ContainedButtonVariant.YesImSure}
            secondaryButtonLabel={TextButtonVariant.NoCancel}
            disablePrimaryButton={loadingIds.has(updateUserScreeningLoadingId)}
            secondaryClick={() => {
              onClose(false)
            }}
          />
        }
        isOpen={isOpen}
        handleFormSubmit={() => {
          triggerUpdateUserScreening()
        }}
      />

      <UpdateUserScreeningWarningModal
        isOpen={showHandleSuccessModal}
        warningMessage={warningMessage}
        onClose={() => {
          setShowHandleWarningModal(false)
          setWarningMessage(undefined)
        }}
      />
    </>
  )
}

export default ConfirmUserScreeningStatusUpdateModal
